import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';

/**
 * AddSiblingButton - Small circular '+' button at the bottom-right corner of the
 * selected element's blue outline. Clicking it triggers adding a sibling element
 * after the selected one.
 *
 * Positioned in the parent React app (outside the iframe) using the same
 * coordinate-mapping technique as ElementToolbar.
 *
 * @param {Object} props
 * @param {React.RefObject} props.iframeRef - Reference to the iframe element
 * @param {string} props.selectedElementPath - Path of the selected element
 * @param {string} props.selectedSectionId - ID of the selected section
 * @param {number} props.zoomLevel - Current zoom level (for position calculation)
 * @param {Function} props.onClick - Callback when the button is clicked
 */
export function AddSiblingButton({
  iframeRef,
  selectedElementPath,
  selectedSectionId,
  zoomLevel = 100,
  onClick,
}) {
  const [position, setPosition] = useState({ top: 0, left: 0, visible: false });
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!iframeRef?.current || (!selectedElementPath && !selectedSectionId)) {
      setPosition(prev => ({ ...prev, visible: false }));
      return;
    }

    const updatePosition = () => {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) return;

      // Find the selected element in iframe, scoped within the correct section
      let element = null;
      if (selectedElementPath && selectedSectionId) {
        const section = iframeDoc.querySelector(`[data-section-id="${selectedSectionId}"]`);
        if (section) {
          element = section.querySelector(`[data-element-id="${selectedElementPath}"]`);
        }
      } else if (selectedSectionId) {
        element = iframeDoc.querySelector(`[data-section-id="${selectedSectionId}"]`);
      }

      if (!element) {
        setPosition(prev => ({ ...prev, visible: false }));
        return;
      }

      // Get element's position within iframe document
      const elementRect = element.getBoundingClientRect();

      // Get iframe's position in the parent window
      const iframeRect = iframe.getBoundingClientRect();

      // Calculate scale factor
      const scale = zoomLevel / 100;

      // Position at the bottom-right corner of the element's outline
      // The outline is drawn at the element boundary, so we position just outside it
      const BUTTON_SIZE = 22;
      const OFFSET = 4; // small gap from the outline corner

      const top = iframeRect.top + (elementRect.top + elementRect.height) * scale + OFFSET;
      const left = iframeRect.left + (elementRect.left + elementRect.width) * scale + OFFSET;

      // Clamp within the container bounds
      const containerRect = iframe.parentElement?.parentElement?.getBoundingClientRect();
      if (!containerRect) return;

      setPosition({
        top: Math.min(top, containerRect.bottom - BUTTON_SIZE - 4),
        left: Math.min(left, containerRect.right - BUTTON_SIZE - 4),
        visible: true,
      });
    };

    updatePosition();

    // Update position on scroll or resize
    const container = iframeRef.current?.parentElement?.parentElement;
    if (container) {
      container.addEventListener('scroll', updatePosition);
    }
    window.addEventListener('resize', updatePosition);

    return () => {
      if (container) {
        container.removeEventListener('scroll', updatePosition);
      }
      window.removeEventListener('resize', updatePosition);
    };
  }, [iframeRef, selectedElementPath, selectedSectionId, zoomLevel]);

  if (!position.visible) return null;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title="Add element after"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 41,
        width: '22px',
        height: '22px',
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        backgroundColor: '#3b82f6',
        color: 'white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
        transform: isHovered ? 'scale(1.1)' : 'scale(1)',
        filter: isHovered ? 'brightness(1.1)' : 'brightness(1)',
        transition: 'transform 0.15s ease, filter 0.15s ease',
        userSelect: 'none',
      }}
    >
      <Plus size={12} strokeWidth={2.5} />
    </button>
  );
}

export default AddSiblingButton;
